/**
 * Teslyn Cloud's Flights page (docs/specs/cloud/flights.md): one row per flight of a project with its daily entry's
 * crew, times and hours, its km and its lines, and the flight's lines in detail. Read only; every figure comes from a
 * rule that already exists, so the page shows what Teslyn Field's daily entry, Day view and the Operations Hub show:
 *
 * - the entry of a flight: `isEntryOfFlight` (same date and flight number, the aircraft when both have one), the one
 *   rule Teslyn pairs an entry and its flight by. A mobilisation flight has its entry too (decision 722): its hours,
 *   crew and comments show, and the totals count its hours like any flight's, ferry hours included (decision 748).
 *   The survey figures elsewhere leave it out (`entryLineStatuses`);
 * - hours: the daily entry's survey, comp box, cals and ferry hours, which Teslyn Field fills from the log by the
 *   airborne split (`splitAirborneTime`, decisions 537, 541, 542 and 558) and the crew may correct; flight hours are
 *   `hoursFlown` (survey + comp box + cals + ferry, decision 162). The cloud holds no log, so a flight with no daily
 *   entry has no hours here;
 * - landing: the log's landing when the flight has one, else `landingTime` (take-off + hours flown, TJR Daily
 *   Flight "Land");
 * - km flown: `flownCreditM` (decisions 32, 176, 197); accepted: `acceptedM` (reviewed, `use_km`; ADR-013);
 *   rejected: `reflyM` (a rejected line's km flown, a part-accepted line's km flown less accepted); pending review:
 *   the km flown of the lines not reviewed yet;
 * - QC: the flight's newest Geosoft QC run and its lines' verdicts (`qc_run`, `qc_line_result`);
 * - Actioned (decision 751): that run's Actioned state (`qcActioned`, the rule of Teslyn Field's run list), from its
 *   lines, their acknowledgements (`qc_acceptance`), their Line review rows and the plan.
 */
import { hoursFlown, landingTime } from './aircraft.js';
import { reflyM } from './day-summary.js';
import type { QcLineResult, QcRun, QcVerdict } from './geosoft-qc.js';
import type {
  Acceptance,
  Block,
  DailyEntry,
  Flight,
  IsoDate,
  LineStatus,
  PlannedLine,
  Project,
  Uuid,
} from './model.js';
import { linesRemaining } from './lines-remaining.js';
import type { MapFeature } from './map-layers.js';
import { opsAircraftKey } from './ops.js';
import type { QcAcceptance } from './qc-acceptance.js';
import { qcActioned, type QcActionedLineInput, type QcRunActioned } from './qc-actioned.js';
import { flownCreditM, type LineState } from './progress.js';
import { plannedLineRole, type PlannedLineRole } from './reflights.js';
import { entryCrewName, type EntryCrewSlot } from './staff.js';
import { acceptedM, isEntryOfFlight } from './tjr-adapter.js';

/** The flight's newest QC run in a word: the run failed, a line fails, a line to check, or every line passes. */
export type FlightQcState = 'run_failed' | 'fail' | 'check' | 'pass';

export const FLIGHT_QC_WORD: Readonly<Record<FlightQcState, string>> = {
  run_failed: 'Run failed',
  fail: 'Fail',
  check: 'Check',
  pass: 'Pass',
};

/** A QC run's result as Teslyn Field's Geosoft QC list words it (fails first, then lines to check). */
export function qcRunState(run: Pick<QcRun, 'status' | 'fail_count' | 'warn_count'>): FlightQcState {
  if (run.status === 'failed') return 'run_failed';
  if (run.fail_count > 0) return 'fail';
  if (run.warn_count > 0) return 'check';
  return 'pass';
}

/** A QC run without the spec it was made with (the cloud page does not read it). */
export type QcRunSummary = Omit<QcRun, 'spec'>;

export interface FlightQc {
  readonly run: QcRunSummary;
  readonly state: FlightQcState;
  /** Runs of the flight, the newest included. */
  readonly runs: number;
}

/** The flight's newest live QC run (by start time), or null when Geosoft QC has not been run on it. */
export function flightQc(flightId: Uuid, runs: readonly QcRunSummary[]): FlightQc | null {
  const own = runs.filter((r) => r.deleted_at === null && r.flight_id === flightId);
  if (own.length === 0) return null;
  const run = own.reduce((a, b) => (b.started_at > a.started_at ? b : a));
  return { run, state: qcRunState(run), runs: own.length };
}

export interface FlightCrew {
  readonly slot: EntryCrewSlot;
  readonly name: string;
}

const SLOT_WORD: Readonly<Record<EntryCrewSlot, string>> = {
  pilot: 'Pilot',
  pilot2: 'Pilot 2',
  operator1: 'Operator',
  operator2: 'Operator 2',
};

/** "Pilot", "Pilot 2", "Operator", "Operator 2". */
export const crewSlotWord = (slot: EntryCrewSlot): string => SLOT_WORD[slot];

export interface FlightHours {
  readonly survey_h: number;
  readonly comp_box_h: number;
  readonly cal_h: number;
  readonly ferry_h: number;
  /** Survey + comp box + cals + ferry (`hoursFlown`). */
  readonly total_h: number;
}

export interface FlightKm {
  readonly flown_km: number;
  readonly accepted_km: number;
  readonly rejected_km: number;
  readonly pending_km: number;
}

export interface FlightRow extends FlightKm {
  readonly flight: Flight;
  /** The flight's daily entry, or null when none has been saved for it. */
  readonly entry: DailyEntry | null;
  /** The aircraft's key for the filter (`opsAircraftKey`). */
  readonly aircraftKey: string;
  /** The registration: the flight's, else the aircraft register's; null when neither knows it. */
  readonly rego: string | null;
  readonly crew: readonly FlightCrew[];
  /** `HH:MM`: the daily entry's take-off, else the log's. */
  readonly takeoff: string | null;
  /** `HH:MM`: the log's landing, else the entry's take-off + hours flown. */
  readonly landing: string | null;
  /** Null without a daily entry. */
  readonly hours: FlightHours | null;
  /** Live lines of the flight. */
  readonly lines: number;
  readonly pendingLines: number;
  readonly qc: FlightQc | null;
  /** Its newest QC run's Actioned state (decision 751); null without a QC run. */
  readonly actioned: QcRunActioned | null;
}

const hhmm = (local: string | null | undefined): string | null => {
  const m = local ? /T(\d{2}):(\d{2})/.exec(local) : null;
  return m ? `${m[1]}:${m[2]}` : null;
};

/** The km of a set of line statuses, on the rules above. */
export function flightKm(statuses: readonly LineStatus[]): FlightKm {
  const live = statuses.filter((s) => s.deleted_at === null);
  const pending = live.filter((s) => s.acceptance === 'pending');
  return {
    flown_km: live.reduce((a, s) => a + flownCreditM(s), 0) / 1000,
    accepted_km: acceptedM(live) / 1000,
    rejected_km: reflyM(live) / 1000,
    pending_km: pending.reduce((a, s) => a + flownCreditM(s), 0) / 1000,
  };
}

export interface FlightListInput {
  readonly flights: readonly Flight[];
  readonly entries: readonly DailyEntry[];
  readonly statuses: readonly LineStatus[];
  readonly qcRuns?: readonly QcRunSummary[];
  /** The register's people, for the crew's names (a removed person's kept name otherwise). */
  readonly people?: readonly { readonly id: Uuid; readonly name: string }[];
  /** The register's aircraft, for a flight whose log named no registration. */
  readonly aircraft?: readonly { readonly id: Uuid; readonly registration: string }[];
  /** For the Actioned column (decision 751): the QC runs' line results, their acknowledgements and the plan. */
  readonly qcLines?: readonly QcActionedLineInput[];
  readonly qcAcceptances?: readonly QcAcceptance[];
  readonly plan?: readonly Pick<PlannedLine, 'number' | 'deleted_at'>[];
  /** A reviewer's name by user id, for the Actioned hover. */
  readonly reviewerName?: (userId: Uuid) => string | null;
}

/** Every live flight of the project with its entry, crew, times, hours, km, lines and QC; newest first. */
export function flightRows(input: FlightListInput): FlightRow[] {
  const people = input.people ?? [];
  const regoOf = new Map((input.aircraft ?? []).map((a) => [a.id, a.registration]));
  const byFlight = new Map<Uuid, LineStatus[]>();
  for (const s of input.statuses) {
    if (s.deleted_at !== null) continue;
    byFlight.set(s.flight_id, [...(byFlight.get(s.flight_id) ?? []), s]);
  }
  const live = input.entries.filter((e) => e.deleted_at === null);
  const actioned = qcActioned({
    runs: input.qcRuns ?? [],
    lines: input.qcLines ?? [],
    acceptances: input.qcAcceptances ?? [],
    statuses: input.statuses,
    plan: input.plan ?? [],
    ...(input.reviewerName ? { nameOf: input.reviewerName } : {}),
  });
  return input.flights
    .filter((f) => f.deleted_at === null)
    .map((f) => {
      const entry = live.find((e) => isEntryOfFlight(e, f)) ?? null;
      const own = byFlight.get(f.id) ?? [];
      const crew: FlightCrew[] = [];
      if (entry) {
        for (const slot of ['pilot', 'pilot2', 'operator1', 'operator2'] as const) {
          const name = entryCrewName(people, entry, slot);
          if (name) crew.push({ slot, name });
        }
      }
      const rego = f.rego || (f.aircraft_id ? (regoOf.get(f.aircraft_id) ?? null) : null) || null;
      const qc = flightQc(f.id, input.qcRuns ?? []);
      return {
        flight: f,
        entry,
        aircraftKey: opsAircraftKey(f.aircraft_id, rego),
        rego,
        crew,
        takeoff: entry?.takeoff_time ?? hhmm(f.takeoff_local),
        landing: hhmm(f.landing_local) ?? (entry ? landingTime(entry) : null),
        hours: entry
          ? {
              survey_h: entry.svy_hours,
              comp_box_h: entry.cbx_hours,
              cal_h: entry.cal_hours,
              ferry_h: entry.fry_hours,
              total_h: hoursFlown(entry),
            }
          : null,
        ...flightKm(own),
        lines: own.length,
        pendingLines: own.filter((s) => s.acceptance === 'pending').length,
        qc,
        actioned: qc ? (actioned.get(qc.run.id) ?? null) : null,
      };
    })
    .sort(
      (a, b) =>
        b.flight.date.localeCompare(a.flight.date) ||
        (b.takeoff ?? '').localeCompare(a.takeoff ?? '') ||
        b.flight.flt_num - a.flight.flt_num,
    );
}

export interface FlightFilter {
  /** Inclusive local dates, `YYYY-MM-DD`; empty for no bound. */
  readonly from: IsoDate | '';
  readonly to: IsoDate | '';
  /** An `aircraftKey`, or '' for every aircraft. */
  readonly aircraft: string;
}

export const NO_FLIGHT_FILTER: FlightFilter = { from: '', to: '', aircraft: '' };

/** "The first date is after the last." when the range is the wrong way round; null when it is fine. */
export function flightFilterIssue(f: FlightFilter): string | null {
  return f.from && f.to && f.from > f.to ? 'The first date is after the last.' : null;
}

export function filterFlightRows(rows: readonly FlightRow[], f: FlightFilter): FlightRow[] {
  return rows.filter(
    (r) =>
      (!f.from || r.flight.date >= f.from) &&
      (!f.to || r.flight.date <= f.to) &&
      (!f.aircraft || r.aircraftKey === f.aircraft),
  );
}

export interface FlightAircraftChoice {
  readonly key: string;
  readonly label: string;
}

/** The aircraft the rows flew, for the filter, by registration. */
export function flightAircraftChoices(rows: readonly FlightRow[]): FlightAircraftChoice[] {
  const out = new Map<string, string>();
  for (const r of rows) if (!out.has(r.aircraftKey)) out.set(r.aircraftKey, r.rego ?? 'No aircraft recorded');
  return [...out].map(([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label));
}

export interface FlightTotals extends FlightKm, FlightHours {
  readonly flights: number;
  /** Flights with a daily entry (the hours are theirs). */
  readonly withEntry: number;
  readonly lines: number;
  readonly pendingLines: number;
}

/** The rows' totals; a daily entry that two flights share counts its hours once. */
export function flightTotals(rows: readonly FlightRow[]): FlightTotals {
  const entries = new Map<Uuid, FlightHours>();
  for (const r of rows) if (r.entry && r.hours) entries.set(r.entry.id, r.hours);
  const hours = [...entries.values()];
  const sum = <T>(xs: readonly T[], f: (x: T) => number): number => xs.reduce((a, x) => a + f(x), 0);
  return {
    flights: rows.length,
    withEntry: rows.filter((r) => r.entry !== null).length,
    survey_h: sum(hours, (h) => h.survey_h),
    comp_box_h: sum(hours, (h) => h.comp_box_h),
    cal_h: sum(hours, (h) => h.cal_h),
    ferry_h: sum(hours, (h) => h.ferry_h),
    total_h: sum(hours, (h) => h.total_h),
    flown_km: sum(rows, (r) => r.flown_km),
    accepted_km: sum(rows, (r) => r.accepted_km),
    rejected_km: sum(rows, (r) => r.rejected_km),
    pending_km: sum(rows, (r) => r.pending_km),
    lines: sum(rows, (r) => r.lines),
    pendingLines: sum(rows, (r) => r.pendingLines),
  };
}

/** "Day 285 flight 3": how the screens name a flight. */
export function flightName(f: Pick<Flight, 'jday' | 'flt_num'>): string {
  return `Day ${f.jday} flight ${f.flt_num}`;
}

// ---------------------------------------------------------------- the flight's lines

/** A reflight or patch of a line: flown (on which flight) or still planned. */
export interface LineFollowUp {
  readonly role: Exclude<PlannedLineRole, 'original'>;
  readonly line_number: number;
  /** The flight that flew it; null while it is only planned. */
  readonly flight: Pick<Flight, 'id' | 'jday' | 'flt_num' | 'date'> | null;
  readonly acceptance: Acceptance | null;
}

export interface FlightLine {
  readonly status: LineStatus;
  /** The block's name, or null when the planned line is not in the plan. */
  readonly block: string | null;
  readonly role: PlannedLineRole;
  readonly flown_km: number;
  /** Counted accepted km (`acceptedM` of the one line). */
  readonly accepted_km: number;
  /** The line this one reflies, with the flight that flew it; null for a first flight of a line. */
  readonly reflies: {
    readonly line_number: number;
    readonly flight: Pick<Flight, 'id' | 'jday' | 'flt_num' | 'date'> | null;
  } | null;
  /** Its reflights and patches, flown or planned, in line-number order. */
  readonly followUps: readonly LineFollowUp[];
  /** The newest QC verdict of the line; null when no QC run covered it. */
  readonly qc: QcVerdict | null;
}

export interface FlightLinesInput {
  readonly flight: Pick<Flight, 'id'>;
  /** Every live status of the project (the reflights of this flight's lines may be on later flights). */
  readonly statuses: readonly LineStatus[];
  readonly plan: readonly PlannedLine[];
  readonly blocks: readonly Pick<Block, 'id' | 'name'>[];
  readonly flights: readonly Flight[];
  readonly qcLines?: readonly Pick<
    QcLineResult,
    'line_status_id' | 'verdict' | 'created_at' | 'deleted_at'
  >[];
}

/** The flight's live lines in flying order of their numbers, with block, km, review, refly links and QC verdict. */
export function flightLines(input: FlightLinesInput): FlightLine[] {
  const planById = new Map(input.plan.filter((p) => p.deleted_at === null).map((p) => [p.id, p]));
  const blockName = new Map(input.blocks.map((b) => [b.id, b.name]));
  const flightById = new Map(input.flights.map((f) => [f.id, f]));
  const live = input.statuses.filter((s) => s.deleted_at === null);
  const statusById = new Map(live.map((s) => [s.id, s]));
  const qcOf = new Map<Uuid, { verdict: QcVerdict; at: string }>();
  for (const q of input.qcLines ?? []) {
    if (q.deleted_at !== null || !q.line_status_id) continue;
    const had = qcOf.get(q.line_status_id);
    if (!had || q.created_at > had.at) qcOf.set(q.line_status_id, { verdict: q.verdict, at: q.created_at });
  }
  const flightRef = (id: Uuid): LineFollowUp['flight'] => {
    const f = flightById.get(id);
    return f ? { id: f.id, jday: f.jday, flt_num: f.flt_num, date: f.date } : null;
  };
  /** The original planned line a status's line belongs to. */
  const originalId = (s: LineStatus): Uuid | null => {
    const p = planById.get(s.planned_line_id);
    if (!p) return null;
    return p.reflight_of_line_id ?? p.id;
  };
  return live
    .filter((s) => s.flight_id === input.flight.id)
    .sort((a, b) => a.line_number - b.line_number)
    .map((s) => {
      const planned = planById.get(s.planned_line_id);
      const origin = originalId(s);
      const earlier = s.reflight_of_status_id ? statusById.get(s.reflight_of_status_id) : undefined;
      // its reflights and patches: later versions of the same original line, flown (with their status) or planned
      const later = origin
        ? [...planById.values()].filter((p) => p.reflight_of_line_id === origin && p.number > s.line_number)
        : [];
      const followUps: LineFollowUp[] = later
        .sort((a, b) => a.number - b.number)
        .map((p) => {
          const flown = live
            .filter((x) => x.planned_line_id === p.id || x.line_number === p.number)
            .sort((a, b) => (b.reviewed_at ?? b.created_at).localeCompare(a.reviewed_at ?? a.created_at))[0];
          return {
            role: plannedLineRole(p) as LineFollowUp['role'],
            line_number: p.number,
            flight: flown ? flightRef(flown.flight_id) : null,
            acceptance: flown?.acceptance ?? null,
          };
        });
      return {
        status: s,
        block: planned ? (blockName.get(planned.block_id) ?? null) : null,
        role: planned ? plannedLineRole(planned) : 'original',
        flown_km: flownCreditM(s) / 1000,
        accepted_km: acceptedM([s]) / 1000,
        reflies: earlier ? { line_number: earlier.line_number, flight: flightRef(earlier.flight_id) } : null,
        followUps,
        qc: qcOf.get(s.id)?.verdict ?? null,
      };
    });
}

// ---------------------------------------------------------------- the flight's map

export interface FlightMap {
  /** The flight's lines (the map fits them, so they stand apart at their spacing). */
  readonly features: readonly MapFeature[];
  /** Lines per state now. */
  readonly counts: Readonly<Partial<Record<LineState, number>>>;
  readonly notDrawn: readonly string[];
}

/**
 * The lines one flight flew, drawn as the lines-remaining map draws them (`linesRemaining`, the same colours and
 * words): each planned line the flight flew (the original of a reflight or patch flown on it) once, in its state
 * now from every review of the project. The block boundaries are left out: the map fits what it draws, and a
 * boundary would zoom it out until 100 m lines run together.
 */
export function flightMap(
  flightId: Uuid,
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  blocks: readonly Block[],
  project: Pick<Project, 'epsg'>,
): FlightMap {
  const live = statuses.filter((s) => s.deleted_at === null);
  const planById = new Map(plan.filter((p) => p.deleted_at === null).map((p) => [p.id, p]));
  const originals = new Set<Uuid>();
  for (const s of live) {
    if (s.flight_id !== flightId) continue;
    const p = planById.get(s.planned_line_id);
    if (p) originals.add(p.reflight_of_line_id ?? p.id);
  }
  // the lines and their reflights and patches: the lines' state comes from all of them
  const family = [...planById.values()].filter(
    (p) => originals.has(p.id) || (p.reflight_of_line_id !== null && originals.has(p.reflight_of_line_id)),
  );
  const blockIds = new Set(family.map((p) => p.block_id));
  const mine = blocks.filter((b) => blockIds.has(b.id));
  // every line the flight flew, the completed ones too: this is the flight's map, not the lines remaining (R136)
  const lines = linesRemaining(live, family, mine, project, { completed: true });
  const counts: Partial<Record<LineState, number>> = {};
  for (const b of lines.blocks)
    for (const [state, n] of Object.entries(b.counts) as [LineState, number][])
      if (n > 0) counts[state] = (counts[state] ?? 0) + n;
  return {
    features: lines.features,
    counts,
    notDrawn: lines.notDrawn,
  };
}
